import React, { useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { grey, green, orange, blue } from '@mui/material/colors';

// Gaps (what the rules did not cover) and user corrections (what a user changed on review),
// with how each was resolved. This is the queue of candidate rule changes: a person reads each
// one and records whether it became a rule update, and in which file, or needs no rule.

export const RESOLUTION = {
    open: { label: 'Open', bg: orange[50], color: orange[900] },
    'rule-updated': { label: 'Rule updated', bg: green[50], color: green[800] },
    'no-rule-needed': { label: 'No rule needed', bg: grey[100], color: grey[700] },
};

const SOURCE = { parser: 'Parser', admin: 'Admin', user: 'User on review' };

export const isOpen = f => !f.resolution;

export function findingCounts(row) {
    const gaps = row.gaps || [];
    const corrections = row.corrections || [];
    return {
        gaps: gaps.length,
        openGaps: gaps.filter(isOpen).length,
        corrections: corrections.length,
        openCorrections: corrections.filter(isOpen).length,
    };
}

function Finding({ kind, item, importId, accesstoken, rulesFiles, onSaved }) {
    const status = item.resolution?.status || 'open';
    const [editing, setEditing] = useState(false);
    const [next, setNext] = useState(status);
    const [note, setNote] = useState(item.resolution?.note || '');
    const [rulesFile, setRulesFile] = useState(item.resolution?.rulesFile || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    async function save() {
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.put(`/api/admin/rate-imports/${importId}/${kind}/${item.id}/resolution?accesstoken=${accesstoken}`,
                { status: next, note, rulesFile: next === 'rule-updated' ? rulesFile : null });
            onSaved(data);
            setEditing(false);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setSaving(false);
    }

    const chip = RESOLUTION[status];
    return (
        <Box sx={{ border: `1px solid ${grey[200]}`, borderRadius: '6px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.text}</Typography>
                <Chip size='small' label={chip.label} sx={{ background: chip.bg, color: chip.color, flexShrink: 0 }} />
            </Box>
            <Typography sx={{ fontSize: '12px', color: grey[600] }}>
                {SOURCE[item.source] || item.source}{item.by ? ` · ${item.by}` : ''} · {dayjs(item.created).format('MMM D, YYYY')}
                {item.rule ? ` · about ${item.rule}` : ''}
            </Typography>
            {item.resolution && !editing && (
                <Box sx={{ background: grey[50], borderRadius: '4px', p: 1, fontSize: '13px' }}>
                    <b>{chip.label}</b>{item.resolution.rulesFile ? ` in ${item.resolution.rulesFile}` : ''}
                    {item.resolution.note ? `: ${item.resolution.note}` : ''}
                    <Typography component='div' sx={{ fontSize: '12px', color: grey[600] }}>
                        {item.resolution.by} · {dayjs(item.resolution.at).format('MMM D, YYYY')}
                    </Typography>
                </Box>
            )}
            {editing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField select size='small' label='Resolution' value={next} onChange={e => setNext(e.target.value)} sx={{ minWidth: 180 }}>
                            {Object.entries(RESOLUTION).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                        </TextField>
                        {next === 'rule-updated' && (
                            <TextField select size='small' label='Rules file' value={rulesFile} onChange={e => setRulesFile(e.target.value)} sx={{ minWidth: 220 }}>
                                {rulesFiles.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                            </TextField>
                        )}
                    </Box>
                    {next !== 'open' && (
                        <TextField size='small' multiline minRows={2} label={next === 'rule-updated' ? 'What changed in the rules' : 'Why no rule is needed'}
                            value={note} onChange={e => setNote(e.target.value)} />
                    )}
                    {error && <Alert severity='error'>{error}</Alert>}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size='small' variant='contained' onClick={save} disabled={saving || (next === 'rule-updated' && !rulesFile)}>{saving ? 'Saving…' : 'Save'}</Button>
                        <Button size='small' onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Button size='small' sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setEditing(true)}>
                        {item.resolution ? 'Change resolution' : 'Resolve'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export function FindingsDialog({ row, accesstoken, rulesFiles, onClose, onSaved }) {
    const [text, setText] = useState('');
    const [rule, setRule] = useState('');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(null);

    async function addGap() {
        setAdding(true);
        setError(null);
        try {
            const { data } = await axios.post(`/api/admin/rate-imports/${row.id}/gaps?accesstoken=${accesstoken}`, { text, rule });
            onSaved(row.id, data);
            setText('');
            setRule('');
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setAdding(false);
    }

    const section = (kind, title, items, empty) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{title} <span style={{ color: grey[600], fontWeight: 400 }}>({items.length})</span></Typography>
            {items.length === 0 && <Typography sx={{ fontSize: '13px', color: grey[600] }}>{empty}</Typography>}
            {items.map(item => (
                <Finding key={item.id} kind={kind} item={item} importId={row.id} accesstoken={accesstoken} rulesFiles={rulesFiles}
                    onSaved={data => onSaved(row.id, data)} />
            ))}
        </Box>
    );

    return (
        <Dialog open={true} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>
                Gaps and corrections
                <Typography sx={{ fontSize: '13px', color: grey[600] }}>
                    {row.organizationTitle} · {row.supplier || 'no supplier'} · {row.created ? dayjs(row.created).format('MMM D, YYYY') : ''}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {section('gaps', 'Gaps', row.gaps || [], 'Nothing the rules failed to cover has been recorded for this file.')}
                {section('corrections', 'User corrections', row.corrections || [], 'No changes were made on review. They are recorded here once the review screen exists.')}
                <Box sx={{ borderTop: `1px solid ${grey[200]}`, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>Log a gap</Typography>
                    <Typography sx={{ fontSize: '13px', color: grey[600] }}>Something in this file the rules don't cover. It joins the queue like a gap from the parser.</Typography>
                    <TextField size='small' multiline minRows={2} label='What the rules did not cover' value={text} onChange={e => setText(e.target.value)} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField size='small' label='Rule it concerns (optional)' placeholder='e.g. R14' value={rule} onChange={e => setRule(e.target.value)} sx={{ width: 220 }} />
                        <Button size='small' variant='outlined' onClick={addGap} disabled={adding || !text.trim()}>{adding ? 'Adding…' : 'Add gap'}</Button>
                    </Box>
                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}

export function FindingsCell({ row, onOpen }) {
    const c = findingCounts(row);
    const open = c.openGaps + c.openCorrections;
    const total = c.gaps + c.corrections;
    return (
        <Button size='small' onClick={() => onOpen(row)} sx={{ minWidth: 0, textTransform: 'none', fontSize: '12px', color: open ? orange[900] : total ? green[800] : blue[700] }}>
            {total === 0 ? 'add' : open ? `${open} open / ${total}` : `${total} resolved`}
        </Button>
    );
}
